import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRight, ArrowDown, Download, Mail, Phone, Plane,
} from "lucide-react";
import Layout from "../components/Layout";
import InquiryForm from "../components/InquiryForm";
import SEO from "../components/SEO";
import TwoDoorsExperience from "../components/TwoDoorsExperience";
import TwoDoorsCollapsed from "../components/TwoDoorsCollapsed";
import useTravelAssessment from "../hooks/useTravelAssessment";

/* ─── Images ─────────────────────────────────────────────────────────────── */

const IMG = {
  nose:        "/images/bj400a-nose.png",
  exterior:    "/images/bj400a-exterior.png",
  cabin:       "/images/bj400a-cabin.png",
  cockpit:     "/images/bj400a-cockpit-sunset.png",
  boarding:    "/images/bj400a-boarding.png",
};

/* ─── Data ───────────────────────────────────────────────────────────────── */

const FEATURES = [
  "Nine-place center-club seating with aft lavatory.",
  "Premium black leather with smoked nickel hardware.",
  "Fully renewed cabin from the 2026 interior refurbishment.",
  "External baggage compartment keeps the cabin clear.",
  "Stand-up entry height and a full pressurized cabin.",
];


/* ─── Scroll-reveal ──────────────────────────────────────────────────────── */

const useReveal = (threshold = 0.10, rootMargin = "0px") => {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.intersectionRatio >= threshold) setV(true); },
      { threshold, rootMargin }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold, rootMargin]);
  return [ref, v];
};

/* ─── Elevated list item components ─────────────────────────────────────── */

const FeatureItem = ({ text, animStyle }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      style={{ ...animStyle }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <span style={{
        color: hov ? "#fff" : "rgba(255,255,255,0.84)",
        fontSize: "clamp(15px,1.3vw,17px)", lineHeight: 1.78,
        letterSpacing: hov ? "0.004em" : "0",
        transition: "color 320ms ease, letter-spacing 320ms ease",
      }}>{text}</span>
    </div>
  );
};

/* ─── Tokens ─────────────────────────────────────────────────────────────── */

const mono    = { fontFamily: "IBM Plex Mono, monospace" };
const display = { fontFamily: "Fraunces, serif" };
const PAD     = "clamp(24px,5vw,80px)";

/* Warm aviation-amber accent—used for status labels, stat category names,
   and ownership-finance eyebrows. Distinct from the cyan teal used on
   live data values, so the hierarchy reads at a glance.                    */
const GOLD = "#C8A870";

/* ─── Flight planning reticle ─────────────────────────────────────────────── */
const FlightChart = ({ style, showLabels = true }) => {
  const kx = 350, ky = 440, sc = 0.48;
  const rad = (deg) => deg * Math.PI / 180;
  const pt  = (b, d) => [kx + Math.sin(rad(b)) * d * sc, ky - Math.cos(rad(b)) * d * sc];

  const routes = [
    { code: "ORD", city: "CHICAGO",     b: 32,  d: 560 },
    { code: "BNA", city: "NASHVILLE",   b: 58,  d: 415 },
    { code: "JFK", city: "NEW YORK",    b: 56,  d: 860 },
    { code: "ATL", city: "ATLANTA",     b: 83,  d: 490 },
    { code: "MSY", city: "NEW ORLEANS", b: 112, d: 290 },
    { code: "MIA", city: "MIAMI",       b: 120, d: 794 },
    { code: "DEN", city: "DENVER",      b: 305, d: 602 },
    { code: "HOU", city: "HOUSTON",     b: 235, d: 166 },
  ];

  const tickR = 54;
  const ticks = Array.from({ length: 24 }, (_, i) => i * 15);

  return (
    <svg viewBox="0 0 900 680" fill="none" xmlns="http://www.w3.org/2000/svg"
      aria-hidden style={{ display: "block", width: "100%", height: "100%", overflow: "visible", ...style }}>

      {/* Fine orthogonal grid */}
      {Array.from({ length: 11 }, (_, i) => (
        <line key={`v${i}`} x1={i * 90} y1="0" x2={i * 90} y2="680"
          stroke="rgba(103,232,249,0.024)" strokeWidth="0.5" />
      ))}
      {Array.from({ length: 9 }, (_, i) => (
        <line key={`h${i}`} x1="0" y1={i * 85} x2="900" y2={i * 85}
          stroke="rgba(103,232,249,0.024)" strokeWidth="0.5" />
      ))}

      {/* Range arcs */}
      {[
        { r: 120, label: "250" },
        { r: 240, label: "500" },
        { r: 480, label: "1,000 NM" },
      ].map(({ r, label }) => (
        <g key={r}>
          <circle cx={kx} cy={ky} r={r}
            stroke="rgba(103,232,249,0.052)" strokeWidth="0.5" strokeDasharray="4 9" />
          <text x={kx + r + 5} y={ky + 4} fontSize="6"
            fill="rgba(103,232,249,0.17)" fontFamily="IBM Plex Mono, monospace" letterSpacing="0.04em">
            {label}
          </text>
        </g>
      ))}

      {/* Bearing radials—one per route, drawn first so markers sit on top */}
      {routes.map(({ code, b, d }) => {
        const [rx, ry] = pt(b, d);
        return (
          <line key={`ray-${code}`} x1={kx} y1={ky} x2={rx} y2={ry}
            stroke="rgba(103,232,249,0.10)" strokeWidth="0.55" strokeDasharray="2 8" />
        );
      })}

      {/* Route position ticks—subtle navigational marks only. The IATA
          code + city name text is opt-in via showLabels: now that each
          destination cell has its own dedicated reticle on hover, repeating
          a sparse handful of city labels here read as an odd, redundant
          pattern rather than a refined instrument chart. */}
      {routes.map(({ code, city, b, d }) => {
        const [rx, ry] = pt(b, d);
        const right = rx >= kx;
        const lx = right ? rx + 9 : rx - 9;
        const anchor = right ? "start" : "end";
        return (
          <g key={code}>
            <line
              x1={rx + Math.cos(rad(b)) * 5} y1={ry + Math.sin(rad(b)) * 5}
              x2={rx - Math.cos(rad(b)) * 5} y2={ry - Math.sin(rad(b)) * 5}
              stroke="rgba(103,232,249,0.50)" strokeWidth="0.9" />
            {showLabels && (
              <>
                <text x={lx} y={ry + 4} fontSize="8.5" fill="rgba(103,232,249,0.46)"
                  fontFamily="IBM Plex Mono, monospace" letterSpacing="0.07em" textAnchor={anchor}>{code}</text>
                <text x={lx} y={ry + 15} fontSize="6" fill="rgba(103,232,249,0.24)"
                  fontFamily="IBM Plex Mono, monospace" letterSpacing="0.05em" textAnchor={anchor}>{city}</text>
              </>
            )}
          </g>
        );
      })}

      {/* Compass rose ticks */}
      {ticks.map(deg => {
        const isCard  = deg % 90  === 0;
        const isOrd   = deg % 45  === 0 && !isCard;
        const inner   = tickR;
        const outer   = tickR + (isCard ? 10 : isOrd ? 6 : 4);
        const [x1, y1] = pt(deg, inner / sc);
        const [x2, y2] = pt(deg, outer / sc);
        return (
          <line key={`t${deg}`}
            x1={x1.toFixed(1)} y1={y1.toFixed(1)} x2={x2.toFixed(1)} y2={y2.toFixed(1)}
            stroke={isCard ? "rgba(103,232,249,0.38)" : "rgba(103,232,249,0.16)"}
            strokeWidth={isCard ? 0.9 : 0.5} />
        );
      })}

      {/* Cardinal degree labels around compass */}
      {[
        { deg: 0,   label: "000°", dx:  0, dy: -8 },
        { deg: 90,  label: "090°", dx:  8, dy:  0 },
        { deg: 180, label: "180°", dx:  0, dy:  8 },
        { deg: 270, label: "270°", dx: -8, dy:  0 },
      ].map(({ deg, label, dx, dy }) => {
        const [lx, ly] = pt(deg, (tickR + 20) / sc);
        return (
          <text key={label} x={lx + dx} y={ly + dy + 4} fontSize="5.5"
            fill="rgba(103,232,249,0.22)" fontFamily="IBM Plex Mono, monospace"
            letterSpacing="0.04em" textAnchor="middle">{label}</text>
        );
      })}

      {/* Departure reticle */}
      <circle cx={kx} cy={ky} r="5.5" stroke="rgba(103,232,249,0.62)" strokeWidth="0.9" fill="rgba(103,232,249,0.06)" />
      <circle cx={kx} cy={ky} r="1.8" fill="rgba(103,232,249,0.94)" />
      <line x1={kx - tickR + 6} y1={ky} x2={kx - 8} y2={ky} stroke="rgba(103,232,249,0.40)" strokeWidth="0.7" />
      <line x1={kx + 8} y1={ky} x2={kx + tickR - 6} y2={ky} stroke="rgba(103,232,249,0.40)" strokeWidth="0.7" />
      <line x1={kx} y1={ky - tickR + 6} x2={kx} y2={ky - 8} stroke="rgba(103,232,249,0.40)" strokeWidth="0.7" />
      <line x1={kx} y1={ky + 8} x2={kx} y2={ky + tickR - 6} stroke="rgba(103,232,249,0.40)" strokeWidth="0.7" />

      {/* Departure label */}
      <text x={kx + 10} y={ky - 6} fontSize="6" fill="rgba(103,232,249,0.36)"
        fontFamily="IBM Plex Mono, monospace" letterSpacing="0.06em">BJ400A · DEPARTURE</text>

      {/* True-north indicator (top-left) */}
      <line x1="44" y1="58" x2="44" y2="34" stroke="rgba(103,232,249,0.44)" strokeWidth="0.9" />
      <polygon points="44,30 41,39 47,39" fill="rgba(103,232,249,0.44)" />
      <text x="44" y="70" fontSize="6.5" fill="rgba(103,232,249,0.30)"
        fontFamily="IBM Plex Mono, monospace" letterSpacing="0.04em" textAnchor="middle">T·N</text>

      {/* Scale bar—250 NM = 120px */}
      <line x1="44" y1="648" x2="44" y2="638" stroke="rgba(103,232,249,0.28)" strokeWidth="0.8" />
      <line x1="44" y1="643" x2="164" y2="643" stroke="rgba(103,232,249,0.22)" strokeWidth="0.5" />
      <line x1="164" y1="648" x2="164" y2="638" stroke="rgba(103,232,249,0.28)" strokeWidth="0.8" />
      <text x="104" y="635" fontSize="6" fill="rgba(103,232,249,0.22)"
        fontFamily="IBM Plex Mono, monospace" letterSpacing="0.05em" textAnchor="middle">250 NM</text>
    </svg>
  );
};
const TexasMap = FlightChart;

/* ─── Vintage grain overlay ──────────────────────────────────────────────────
   SVG feTurbulence noise tiled at low opacity with overlay blend.
   Gives photos a subtle film-grain / silver-print texture.
─────────────────────────────────────────────────────────────────────────── */
const GRAIN_URL = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.78' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23g)'/%3E%3C/svg%3E")`;
const VintageGrain = ({ z = 10, opacity = 0.048 }) => (
  <div aria-hidden style={{
    position: "absolute", inset: 0, pointerEvents: "none",
    backgroundImage: GRAIN_URL, backgroundSize: "160px 160px",
    mixBlendMode: "overlay", opacity, zIndex: z,
  }} />
);

/* ═══════════════════════════════════════════════════════════════════════════
   GLOBAL ANIMATIONS
   Four Ken Burns directions so each section drifts a different way.
═══════════════════════════════════════════════════════════════════════════ */

const ANIM_STYLES = `
  @keyframes kb            { from{transform:scale(1.08)}                             to{transform:scale(1.0)} }
  @keyframes kb-slow       { from{transform:scale(1.05)}                             to{transform:scale(1.0)} }
  @keyframes kb-right      { from{transform:scale(1.07) translateX(-1.2%)}           to{transform:scale(1.0) translateX(0)} }
  @keyframes kb-up         { from{transform:scale(1.06) translateY(1.4%)}            to{transform:scale(1.0) translateY(0)} }
  @keyframes kb-tl         { from{transform:scale(1.07) translate(0.8%, 0.8%)}       to{transform:scale(1.0) translate(0,0)} }
  @keyframes fadeUp        { from{opacity:0;transform:translateY(18px)}              to{opacity:1;transform:translateY(0)} }
  @keyframes slideUp       { from{opacity:0;transform:translateY(34px)}              to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn        { from{opacity:0}                                         to{opacity:1} }
  @keyframes commandReveal { from{opacity:0;transform:translateY(22px) translateX(-50%);letter-spacing:-0.04em} to{opacity:1;transform:translateY(0) translateX(-50%);letter-spacing:-0.01em} }
  @keyframes lineGrow      { from{transform:scaleX(0);transform-origin:left center}  to{transform:scaleX(1);transform-origin:left center} }
  @keyframes slideUpLg     { from{opacity:0;transform:translateY(52px)}              to{opacity:1;transform:translateY(0)} }
  @keyframes cabin-runway  { from{transform:scale(1.0) translateY(0%)}               to{transform:scale(1.12) translateY(-4%)} }
  @keyframes cabin-text-in { from{opacity:0;transform:translateX(-22px)}             to{opacity:1;transform:translateX(0)} }

  /* Cost CTA—filled gold, sharp-cornered to match .btn-primary/.btn-ghost
     in the same row rather than standing apart as a rounded pill. Filled
     (not just outlined) so it reads unmistakably as the button worth
     clicking, with a quiet ring-pulse to keep the "look here first" cue. */
  .btn-cost {
    display: inline-flex; align-items: center; gap: 0.7rem;
    padding: 0.95rem 1.8rem;
    border-radius: 0;
    background: linear-gradient(135deg, #EAC97E 0%, #C9A45C 100%);
    border: 1px solid #C9A45C;
    box-shadow: 0 14px 30px -14px rgba(200,168,112,0.6);
    color: #171106;
    font-family: 'Inter', sans-serif;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0;
    text-decoration: none;
    cursor: pointer;
    animation: btn-cost-pulse 3.4s ease-in-out infinite;
    transition: background 250ms ease, border-color 250ms ease, transform 250ms ease, box-shadow 250ms ease;
  }
  .btn-cost svg { transition: transform 280ms ease; }
  .btn-cost:hover {
    background: linear-gradient(135deg, #F3D794 0%, #D6B06C 100%);
    transform: translateY(-1px);
    box-shadow: 0 18px 38px -14px rgba(200,168,112,0.72);
  }
  .btn-cost:hover svg { transform: translateY(2px); }
  .btn-cost:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(200,168,112,0.4), 0 14px 30px -14px rgba(200,168,112,0.6);
  }
  @keyframes btn-cost-pulse {
    0%, 100% {
      box-shadow: 0 14px 30px -14px rgba(200,168,112,0.6), 0 0 0 0 rgba(200,168,112,0.35);
    }
    50% {
      box-shadow: 0 14px 30px -14px rgba(200,168,112,0.6), 0 0 0 8px rgba(200,168,112,0);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .btn-cost { animation: none; }
  }
`;

/* ═══════════════════════════════════════════════════════════════════════════
   CINEMATIC PHOTO BASE
   Renders the image + standard cinematic vignette into position:absolute inset 0.
   Sections add their own directional mask layers on top.
═══════════════════════════════════════════════════════════════════════════ */

const CinematicPhoto = ({
  src, alt = "",
  brightness = 0.72, saturate = 0.78,
  objectPosition = "center center",
  kenBurns = "kb-slow", kenBurnsDuration = "14s",
  feather = null,          // CSS mask-image—shapes how the photo dissolves into black
  featherComposite = null, // "source-in" when feather has multiple gradients that need intersecting
}) => {
  const [ref, visible] = useReveal(0.04);
  return (
    <div ref={ref} style={{
      position: "absolute", inset: 0, overflow: "hidden",
      ...(feather ? {
        WebkitMaskImage: feather,
        maskImage: feather,
        ...(featherComposite ? {
          WebkitMaskComposite: featherComposite,
          maskComposite: featherComposite === "source-in" ? "intersect" : featherComposite,
        } : {}),
      } : {}),
    }}>
      <img
        src={src} alt={alt} loading="lazy"
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          objectFit: "cover", objectPosition,
          filter: `brightness(${brightness}) contrast(1.18) saturate(${saturate})`,
          opacity: visible ? 1 : 0,
          animation: visible ? `${kenBurns} ${kenBurnsDuration} ease-out both` : "none",
          transition: "opacity 1.8s ease",
          transformOrigin: "center center",
          willChange: "transform, opacity",
        }}
      />
      {/* Subtle corner vignette—feather mask handles the artistic dissolve shape */}
      <div aria-hidden style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "radial-gradient(ellipse 88% 82% at 50% 50%, transparent 28%, rgba(0,0,0,0.28) 60%, rgba(0,0,0,0.62) 82%, #000 100%)",
      }} />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CABIN ASSEMBLE
   4×4 grid of photo tiles that converge as the section enters view.
   Shards start scattered (displaced) and click together as you scroll in,
   revealing the interior. Text swoops in after the photo assembles.
═══════════════════════════════════════════════════════════════════════════ */

const SHARDS = [
  { r:0, c:0, tx:-9,  ty:-12, rot:-9  }, { r:0, c:1, tx:-2,  ty:-14, rot:-4  },
  { r:0, c:2, tx: 3,  ty:-13, rot: 5  }, { r:0, c:3, tx:10,  ty:-11, rot: 8  },
  { r:1, c:0, tx:-12, ty: -3, rot:-7  }, { r:1, c:1, tx: -3, ty: -1, rot: 3  },
  { r:1, c:2, tx:  5, ty: -2, rot:-4  }, { r:1, c:3, tx: 13, ty: -1, rot: 6  },
  { r:2, c:0, tx:-11, ty:  5, rot: 7  }, { r:2, c:1, tx: -4, ty:  2, rot:-3  },
  { r:2, c:2, tx:  4, ty:  4, rot: 4  }, { r:2, c:3, tx: 12, ty:  4, rot:-5  },
  { r:3, c:0, tx: -8, ty: 12, rot:-8  }, { r:3, c:1, tx: -1, ty: 15, rot: 5  },
  { r:3, c:2, tx:  5, ty: 13, rot:-6  }, { r:3, c:3, tx:  9, ty: 11, rot: 7  },
];

const CabinShatter = () => {
  const containerRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf;
    const tick = () => {
      raf = null;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.max(0, Math.min(1, (vh * 0.88 - rect.top) / (vh * 0.52)));
      setProgress(p);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(tick); };
    window.addEventListener("scroll", onScroll, { passive: true });
    const initRaf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(initRaf);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div ref={containerRef} style={{
      position: "relative", height: "80vh", overflow: "hidden",
      marginTop: "clamp(0px,1.5vh,24px)",
    }}>
      {SHARDS.map(({ r, c, tx, ty, rot }, idx) => {
        // Per-shard diagonal wave: top-left assembles first, bottom-right last.
        // Opacity fades in alongside position so shards dissolve into place
        // rather than appearing as hard geometric tiles.
        const wave = (r + c) * 0.042;
        const sp   = Math.max(0, Math.min(1, (progress - wave) / 0.72));
        const disp = 1 - sp; // 1=scattered, 0=home
        return (
          <div key={idx} style={{
            position: "absolute", inset: 0,
            clipPath: `inset(${r * 25}% ${100 - (c + 1) * 25}% ${100 - (r + 1) * 25}% ${c * 25}%)`,
            transform: `translate(${tx * disp}vw, ${ty * disp}vh) rotate(${rot * disp}deg)`,
            transformOrigin: `${c * 25 + 12.5}% ${r * 25 + 12.5}%`,
            opacity: sp,
            willChange: "transform, opacity",
            zIndex: 2,
          }}>
            <img src={IMG.cabin} alt="" aria-hidden style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "76% 26%",
              filter: "brightness(0.80) contrast(1.14) saturate(0.56) sepia(0.09)",
              display: "block",
            }} />
          </div>
        );
      })}

      {/* Deep left vignette for text depth + radial corner darkening + top/bottom
          anchor—both edges now resolve to true solid black so the photo
          dissolves cleanly into the black sections above and below it,
          rather than cutting off hard at its own frame edge. */}
      <div aria-hidden style={{
        position: "absolute", inset: 0, zIndex: 8, pointerEvents: "none",
        background: [
          "linear-gradient(to right, rgba(0,0,0,0.97) 0%, rgba(0,0,0,0.82) 22%, rgba(0,0,0,0.38) 48%, rgba(0,0,0,0.06) 66%, transparent 78%)",
          "linear-gradient(to bottom, #000 0%, transparent 16%)",
          "linear-gradient(to top,    #000 0%, rgba(0,0,0,0.45) 22%, transparent 46%)",
          "radial-gradient(ellipse 92% 92% at 62% 50%, transparent 28%, rgba(0,0,0,0.22) 58%, rgba(0,0,0,0.68) 82%, #000 100%)",
        ].join(", "),
      }} />

      {(() => {
        const tp = Math.min(1, Math.max(0, (progress - 0.52) * 3.2));
        return (
          <div style={{
            position: "absolute", left: PAD, top: "50%",
            zIndex: 20, pointerEvents: "none",
            maxWidth: "clamp(300px,40vw,520px)",
            opacity: tp,
            transform: `translateY(-50%) translateX(${(1 - tp) * -32}px)`,
          }}>
            <h3 style={{
              ...display, fontSize: "clamp(42px,5.8vw,88px)",
              fontWeight: 900, color: "#fff", margin: "0 0 clamp(20px,2.8vh,32px)", lineHeight: 0.92,
              letterSpacing: "-0.022em",
            }}>
              What&rsquo;s<br />Inside.
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "clamp(14px,1.9vh,24px)" }}>
              {FEATURES.map((feat, i) => {
                const fp = Math.min(1, Math.max(0, (progress - 0.56) * 3.2 - i * 0.10));
                return (
                  <FeatureItem
                    key={i}
                    text={feat}
                    animStyle={{ opacity: fp, transform: `translateX(${(1 - fp) * -18}px)` }}
                  />
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   COMMAND—static cockpit photo with feathered edges, editorial text overlay
═══════════════════════════════════════════════════════════════════════════ */

const CommandIris = ({ commandVisible }) => (
  <div className="command-iris" style={{
    position: "relative", overflow: "hidden", background: "#000",
    minHeight: "clamp(540px,64vh,800px)",
    opacity: commandVisible ? undefined : 0,
    animation: commandVisible ? "fadeIn 1.0s ease-out both" : "none",
  }}>
    {/* Cockpit photo—laid in at full width and its NATURAL height (no crop,
        no letterbox). Because the image fills its own box, its real top edge
        sits flush at the top of the section, so the top feather below actually
        catches that edge—rather than a fade floating above a hard image edge,
        which is exactly what a centered `contain` fit produced. The wrapper
        carries the soft left/right feather; the top/bottom fades embed those
        edges into the black. */}
    <div className="command-photo" style={{
      position: "relative", zIndex: 0,
      WebkitMaskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.5) 7%, #000 15%, #000 85%, rgba(0,0,0,0.5) 93%, transparent 100%)",
      maskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.5) 7%, #000 15%, #000 85%, rgba(0,0,0,0.5) 93%, transparent 100%)",
    }}>
      <img src={IMG.cockpit} alt="" aria-hidden style={{
        display: "block", width: "100%", height: "auto",
        filter: "brightness(0.97) contrast(1.05) saturate(0.94)",
      }} />
    </div>

    {/* Left + bottom gradient—text readability */}
    <div aria-hidden className="cmd-readability" style={{
      position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none",
      background: [
        "linear-gradient(to right, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.74) 28%, rgba(0,0,0,0.26) 52%, transparent 70%)",
        "linear-gradient(to top,   rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.62) 36%, rgba(0,0,0,0.20) 58%, transparent 75%)",
      ].join(", "),
    }} />

    {/* Editorial text block */}
    <div className="command-text" style={{
      position: "absolute", bottom: "clamp(52px,7.5vh,120px)", left: PAD,
      zIndex: 4, maxWidth: "clamp(300px,40vw,540px)",
      opacity: commandVisible ? 1 : 0,
      animation: commandVisible ? "slideUp 1.0s 0.40s ease-out both" : "none",
      pointerEvents: "none",
    }}>
      <h2 style={{
        ...display, fontSize: "clamp(42px,5.8vw,88px)", fontWeight: 900,
        color: "#fff", margin: "0 0 clamp(20px,2.8vh,32px)",
        lineHeight: 0.92, letterSpacing: "-0.022em",
      }}>
        Command.
      </h2>

      <p style={{
        color: "rgba(255,255,255,0.74)", fontSize: "clamp(15px,1.3vw,17px)",
        lineHeight: 1.78, margin: "0 0 clamp(16px,2vh,24px)",
      }}>
        The Garmin G5000 doesn&rsquo;t approximate&mdash;it resolves. Three 10.4-inch
        EFIS touchscreens, a GFC&nbsp;700 autopilot that couples to WAAS/LPV
        approaches with sub-50-foot accuracy, and ADS-B Out across every airspace
        class.
      </p>

      <div style={{ marginBottom: "clamp(14px,1.8vh,22px)" }}>
        <div style={{ ...mono, fontSize: "clamp(16px,1.4vw,22px)", color: "#67E8F9", letterSpacing: "0.12em", fontWeight: 500, lineHeight: 1 }}>
          GARMIN G5000
        </div>
        <div style={{ ...mono, fontSize: 13, color: "rgba(255,255,255,0.62)", letterSpacing: "0.10em", marginTop: 7, fontWeight: 400 }}>
          INTEGRATED AVIONICS SUITE
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "clamp(8px,0.9vw,12px)" }}>
        {["GFC 700", "WAAS/LPV", "ADS-B OUT"].map((t) => (
          <span key={t} style={{
            ...mono, fontSize: 13, letterSpacing: "0.07em", fontWeight: 500,
            color: "rgba(255,255,255,0.82)", whiteSpace: "nowrap",
            padding: "8px 14px", borderRadius: 6,
            border: "1px solid rgba(103,232,249,0.28)",
            background: "rgba(103,232,249,0.05)",
          }}>
            {t}
          </span>
        ))}
      </div>
    </div>

    <VintageGrain z={3} opacity={0.044} />
    {/* Top edge feather—now that the photo's real top edge sits flush at the
        top, this feathers just that tip: solid black exactly at the edge,
        easing off quickly so it reads as a subtle fade-from-black, not a heavy
        veil over the sunset. */}
    <div aria-hidden style={{
      position: "absolute", top: 0, left: 0, right: 0, height: "clamp(88px,15%,180px)",
      background: "linear-gradient(to bottom, #000 0%, rgba(0,0,0,0.6) 32%, rgba(0,0,0,0.18) 64%, transparent 100%)",
      pointerEvents: "none", zIndex: 2,
    }} />
    {/* Bottom fade—embeds the photo's bottom edge into the black. */}
    <div aria-hidden className="cmd-bottomfade" style={{
      position: "absolute", bottom: 0, left: 0, right: 0, height: "18%",
      background: "linear-gradient(to top, #000 0%, rgba(0,0,0,0.45) 36%, transparent 100%)",
      pointerEvents: "none", zIndex: 2,
    }} />
    <style>{`
      /* Mobile: the wide cockpit becomes a full-width banner and the copy
         stacks cleanly BELOW it, instead of overlaying a thin sliver. The
         photo's bottom edge feathers into black (bottom-fade mask), and the
         desktop-only text-on-photo overlay gradients are dropped. */
      @media (max-width: 760px) {
        .command-iris { min-height: 0 !important; }
        .command-photo {
          -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 76%, transparent 100%) !important;
          mask-image: linear-gradient(to bottom, #000 0%, #000 76%, transparent 100%) !important;
        }
        .cmd-readability, .cmd-bottomfade { display: none !important; }
        .command-text {
          position: static !important; inset: auto !important; max-width: none !important;
          padding: clamp(16px,4.5vw,28px) ${PAD} clamp(40px,10vw,60px) !important;
          opacity: 1 !important;
        }
      }
    `}</style>
  </div>
);


/* ═══════════════════════════════════════════════════════════════════════════
   CITY RETICLE
   A small self-contained targeting reticle + IATA code, correct to whatever
   city's cell it sits in—revealed on hover instead of relying on a shared
   background map that may not line up with the cell above it. The range-ring
   radius and bearing tick are derived from that city's own distance/bearing,
   so every cell's reticle reads as genuinely its own, not a repeated stamp.
═══════════════════════════════════════════════════════════════════════════ */

const CityReticle = ({ code, city, bearing, miles }) => {
  const rad = (bearing * Math.PI) / 180;
  // Destination point drifts opposite its own bearing, and the home/departure
  // point sits out along that real bearing at a distance scaled to this city's
  // actual mileage—so every reticle is framed like a different crop of the
  // same chart: close cities feel tightly zoomed with both points in frame,
  // distant ones push "home" toward (or past) the edge, cropped by the panel.
  const dx = 100 - Math.sin(rad) * 16;
  const dy = 62 + Math.cos(rad) * 9;
  const sep = 26 + Math.min(miles / 1000, 1) * 84;
  const hx = dx + Math.sin(rad) * sep;
  const hy = dy - Math.cos(rad) * sep;
  const r = 16 + Math.min(miles / 1000, 1) * 7;
  const tx1 = dx + Math.sin(rad) * r;
  const ty1 = dy - Math.cos(rad) * r;
  const tx2 = dx + Math.sin(rad) * (r + 7);
  const ty2 = dy - Math.cos(rad) * (r + 7);
  const lx  = dx + Math.sin(rad) * (r + 15);
  const ly  = dy - Math.cos(rad) * (r + 15);
  return (
    <svg viewBox="0 0 200 120" className="routes-grid__reticle" aria-hidden>
      {/* Faint backdrop grid—gives every reticle the same chart texture,
          regardless of where its cell happens to sit. */}
      <line x1="0" y1="26" x2="200" y2="26" stroke="rgba(103,232,249,0.05)" strokeWidth="0.5" />
      <line x1="0" y1="94" x2="200" y2="94" stroke="rgba(103,232,249,0.05)" strokeWidth="0.5" />
      <line x1="66" y1="0" x2="66" y2="120" stroke="rgba(103,232,249,0.045)" strokeWidth="0.5" />
      <line x1="133" y1="0" x2="133" y2="120" stroke="rgba(103,232,249,0.045)" strokeWidth="0.5" />

      {/* Bearing line—destination back to the shared home/departure point,
          along this city's own real bearing and distance */}
      <line x1={(dx + Math.sin(rad) * 6).toFixed(1)} y1={(dy - Math.cos(rad) * 6).toFixed(1)}
            x2={(hx - Math.sin(rad) * 5).toFixed(1)} y2={(hy + Math.cos(rad) * 5).toFixed(1)}
            stroke="rgba(103,232,249,0.16)" strokeWidth="0.5" strokeDasharray="1.5 4" />

      {/* Range ring—radius reflects this city's own distance */}
      <circle cx={dx.toFixed(1)} cy={dy.toFixed(1)} r={r} stroke="rgba(103,232,249,0.22)" strokeWidth="0.6" strokeDasharray="1.5 4.5" fill="none" />
      {/* Bearing tick + degree label—unique angle per city */}
      <line x1={tx1.toFixed(1)} y1={ty1.toFixed(1)} x2={tx2.toFixed(1)} y2={ty2.toFixed(1)} stroke="rgba(103,232,249,0.5)" strokeWidth="0.6" />
      <text x={lx.toFixed(1)} y={(ly + 2).toFixed(1)} fontSize="5.5" fontFamily="'IBM Plex Mono', monospace" fill="rgba(103,232,249,0.40)" textAnchor="middle" letterSpacing="0.03em">
        {String(Math.round(bearing)).padStart(3, "0")}&deg;
      </text>

      {/* Destination reticle */}
      <circle cx={dx.toFixed(1)} cy={dy.toFixed(1)} r="4.5" stroke="rgba(103,232,249,0.75)" strokeWidth="0.8" fill="rgba(103,232,249,0.08)" />
      <circle cx={dx.toFixed(1)} cy={dy.toFixed(1)} r="1.3" fill="rgba(103,232,249,0.95)" />
      <line x1={dx.toFixed(1)} y1={(dy - 44).toFixed(1)} x2={dx.toFixed(1)} y2={(dy - 8).toFixed(1)}  stroke="rgba(103,232,249,0.35)" strokeWidth="0.6" />
      <line x1={dx.toFixed(1)} y1={(dy + 8).toFixed(1)}  x2={dx.toFixed(1)} y2={(dy + 44).toFixed(1)} stroke="rgba(103,232,249,0.35)" strokeWidth="0.6" />
      <line x1={(dx - 44).toFixed(1)} y1={dy.toFixed(1)} x2={(dx - 8).toFixed(1)} y2={dy.toFixed(1)}  stroke="rgba(103,232,249,0.35)" strokeWidth="0.6" />
      <text x={dx.toFixed(1)} y={(dy + 32).toFixed(1)} fontSize="9" fontFamily="'IBM Plex Mono', monospace" fill="rgba(103,232,249,0.85)" textAnchor="middle" letterSpacing="0.05em">{code}</text>
      <text x={dx.toFixed(1)} y={(dy + 42).toFixed(1)} fontSize="5.5" fontFamily="'IBM Plex Mono', monospace" fill="rgba(103,232,249,0.45)" textAnchor="middle" letterSpacing="0.06em">{city.toUpperCase()}</text>

      {/* Home / departure reference—same real anchor for every city, but
          framed differently each time depending on that city's own bearing
          and distance, the same way every route on the full chart returns
          to one home base from a different direction. */}
      <circle cx={hx.toFixed(1)} cy={hy.toFixed(1)} r="3.2" stroke="rgba(103,232,249,0.55)" strokeWidth="0.6" fill="rgba(103,232,249,0.05)" />
      <circle cx={hx.toFixed(1)} cy={hy.toFixed(1)} r="1" fill="rgba(103,232,249,0.75)" />
      <line x1={hx.toFixed(1)} y1={(hy - 18).toFixed(1)} x2={hx.toFixed(1)} y2={(hy - 7).toFixed(1)} stroke="rgba(103,232,249,0.24)" strokeWidth="0.5" />
      <line x1={hx} y1={hy + 7}  x2={hx} y2={hy + 18} stroke="rgba(103,232,249,0.24)" strokeWidth="0.5" />
      <text x={hx} y={hy + 30} fontSize="5" fontFamily="'IBM Plex Mono', monospace" fill="rgba(103,232,249,0.32)" textAnchor="middle" letterSpacing="0.05em">
        BJ400A &middot; DEPARTURE
      </text>
    </svg>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CABIN WALKTHROUGH — scroll-scrubbed interior walk (cabin → flight deck)

   A slim, ultra-wide "viewport" strip that lives between What's Inside and
   The Envelope. On a fine pointer with motion allowed, the clip does NOT
   autoplay; its playback position is bound to how far the visitor has scrolled
   through this section, so they walk the actual airplane from the cabin to the
   flight deck at their own pace—responding to the person rather than competing
   with the hero. On touch / reduced-motion it degrades to a quiet in-view
   autoplay loop. Restrained by shape (a letterbox slit) and by behaviour (it
   only moves when the visitor does). The source is re-encoded all-keyframe so
   every frame is instantly seekable.
═══════════════════════════════════════════════════════════════════════════ */
const CabinWalkthrough = () => {
  const wrapRef  = useRef(null);
  const videoRef = useRef(null);
  const fillRef  = useRef(null);
  // Autoplay-in-view for everyone. The scroll-scrub mode (retained below) pinned
  // ~62vh of scroll and drove the video frame-by-frame off scroll position —
  // which on some machines could freeze the frame while you kept scrolling, so
  // it read as a stuck scroll. Forcing the reliable autoplay path removes that
  // failure mode entirely: the video simply plays when it comes into view.
  const [scrub] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    if (!scrub) {
      // Fallback: quiet in-view autoplay loop (touch / reduced-motion). The
      // progress bar tracks playback time instead of scroll.
      v.loop = true;
      const onTime = () => {
        if (fillRef.current && v.duration) fillRef.current.style.width = `${(v.currentTime / v.duration) * 100}%`;
      };
      v.addEventListener("timeupdate", onTime);
      const io = new IntersectionObserver(
        ([e]) => { e.isIntersecting ? v.play().catch(() => {}) : v.pause(); },
        { threshold: 0.25 },
      );
      io.observe(v);
      return () => { io.disconnect(); v.removeEventListener("timeupdate", onTime); };
    }

    // Scrub mode: bind currentTime to scroll progress through the wrapper.
    //
    // Windows reliability: setting currentTime faster than the browser can
    // finish a seek makes the decoder thrash — each new seek interrupts the
    // previous one, 'seeked' never fires, and the frame stays frozen even
    // though scroll (and the progress bar) keep advancing. macOS seeks fast
    // enough to mask this; Windows does not. So we (1) SERIALIZE seeks — only
    // ever issue the LATEST scroll target once the prior seek has settled —
    // and (2) PRIME the decode pipeline once with a muted play/pause, which
    // makes Windows reliably paint seeked frames. A short watchdog guards
    // against a 'seeked' event that never arrives. All of this is a no-op on
    // platforms where scrubbing already worked.
    v.pause();
    const wrap = wrapRef.current;
    let raf = 0;
    let pendingP = null;     // latest scroll progress awaiting a seek
    let seeking = false;
    let seekStart = 0;

    const applySeek = () => {
      if (pendingP == null || !v.duration) return;
      const target = pendingP * (v.duration - 0.05);
      pendingP = null;
      if (Math.abs(v.currentTime - target) < 0.01) return;
      seeking = true;
      seekStart = performance.now();
      try { v.currentTime = target; } catch (e) { seeking = false; }
    };

    const onSeeked = () => {
      seeking = false;
      // Nudge the compositor to present the just-decoded frame (Windows).
      if (typeof v.requestVideoFrameCallback === "function") {
        try { v.requestVideoFrameCallback(() => {}); } catch (e) { /* ignore */ }
      }
      if (pendingP != null) applySeek();   // a newer scroll target arrived mid-seek
    };

    const update = () => {
      raf = 0;
      if (!wrap || !v.duration) return;
      const rect  = wrap.getBoundingClientRect();
      const total = rect.height - window.innerHeight;      // scroll room for the walk
      const scrolled = Math.min(Math.max(-rect.top, 0), Math.max(total, 0));
      const p = total > 0 ? scrolled / total : 0;
      pendingP = p;
      if (fillRef.current) fillRef.current.style.width = `${p * 100}%`;
      // Don't thrash a seek that's in flight — but if one has been stuck for a
      // while (missed 'seeked'), force the next one so we can never freeze.
      if (!seeking || performance.now() - seekStart > 250) applySeek();
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };

    // Prime the decoder (Windows paints seeked frames far more reliably once
    // the element has been activated by a play() call), then sync to scroll.
    const start = () => {
      const pr = v.play();
      if (pr && typeof pr.then === "function") {
        pr.then(() => v.pause()).catch(() => { try { v.pause(); } catch (e) { /* ignore */ } });
      } else {
        try { v.pause(); } catch (e) { /* ignore */ }
      }
      update();
    };

    v.addEventListener("seeked", onSeeked);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    if (v.readyState >= 2) start();
    else v.addEventListener("loadeddata", start, { once: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      v.removeEventListener("seeked", onSeeked);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrub]);

  return (
    <section
      ref={wrapRef}
      aria-label="Cabin to flight-deck walkthrough of the aircraft"
      style={{ background: "#000", position: "relative", height: scrub ? "162vh" : "auto" }}
    >
      <div style={{
        position: scrub ? "sticky" : "relative", top: 0,
        height: scrub ? "100vh" : "auto",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: scrub ? "0" : "clamp(48px,7vh,88px) 0",
      }}>
        {/* Quiet authenticity label—leans into "this is the real airplane,"
            not a luxury tagline. */}
        <div style={{ ...mono, fontSize: "clamp(11px,0.9vw,13px)", letterSpacing: "0.22em", color: "rgba(255,255,255,0.42)", textTransform: "uppercase", marginBottom: "clamp(16px,2.2vh,28px)", textAlign: "center", padding: `0 ${PAD}` }}>
          Step aboard the aircraft
        </div>

        {/* Ultra-wide letterbox strip, feathered into the black top &amp; bottom
            so it reads as a viewport onto the interior, not a boxed panel. */}
        <div style={{ position: "relative", width: "100%", maxWidth: 1500, margin: "0 auto", aspectRatio: "1280 / 548", maxHeight: "62vh", overflow: "hidden" }}>
          <video
            ref={videoRef}
            muted
            playsInline
            preload="auto"
            aria-hidden="true"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block", transform: "scale(1.02)" }}
          >
            <source src="/cabin-walkthrough.mp4" type="video/mp4" />
          </video>
          <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(to bottom, #000 0%, transparent 11%, transparent 89%, #000 100%)" }} />
        </div>

        {/* Endpoints of the walk: cabin at the start, flight deck at the end. */}
        <div style={{ ...mono, display: "flex", alignItems: "center", gap: 14, fontSize: "clamp(10px,0.8vw,12px)", letterSpacing: "0.16em", color: "rgba(255,255,255,0.34)", textTransform: "uppercase", marginTop: "clamp(16px,2.2vh,28px)" }}>
          <span>Cabin</span>
          {/* Progress track—fills from cabin toward cockpit as the walk
              advances (bound to scroll in scrub mode, to playback time in the
              autoplay fallback). No transition while scrubbing so the bar stays
              frame-synced; a short ease in the fallback smooths the coarser
              timeupdate steps. */}
          <span aria-hidden style={{ position: "relative", width: "clamp(64px,12vw,180px)", height: 2, background: "rgba(255,255,255,0.16)", borderRadius: 2, overflow: "hidden" }}>
            <span ref={fillRef} style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "0%", background: "rgba(125,216,234,0.85)", borderRadius: 2, transition: scrub ? "none" : "width 260ms linear" }} />
          </span>
          <span>Cockpit</span>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════════════════════ */

const Aircraft = () => {
  const [specsRef,   specsVisible]   = useReveal(0.06);
  const [commandRef, commandVisible] = useReveal(0.06);
  // "The Envelope." left-to-right wipe—triggered when it scrolls into view.
  const [envHeadRef, envHeadVisible] = useReveal(0.4);
  // Small bottom rootMargin only: the sweep now starts fresh, from the very
  // beginning of its pass, right as this section reaches the viewport—so a
  // visitor reliably watches the beam cross rather than arriving after a big
  // head start has already carried it off-screen (or into its quiet hold).
  const [routesSheenRef, routesSheenVisible] = useReveal(0.01, "0px 0px 120px 0px");
  const envRef = useRef(null);
  // Shared "scroll-reveal" utility (The Envelope heading, Ownership Basics
  // heading, ...): prefer a true scroll-linked reveal (CSS animation-timeline)
  // so the blur/rise is always in perfect sync with scroll position and
  // can't "finish" off-screen before it's seen. Only browsers without that
  // support fall back to this IntersectionObserver + class toggle, matching
  // the pattern already used on the home page.
  useEffect(() => {
    if (CSS.supports && CSS.supports("animation-timeline: view()")) return;
    const els = document.querySelectorAll(".scroll-reveal");
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in-view");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  // The Two Doors tool is available here (a vestibule destination) via the
  // collapsed tab — but it does NOT auto-open. Force-opening a full-screen
  // overlay on arrival read as a broken/locked scroll, so arrivals just see the
  // page; the tab lets anyone open the tool on their own terms.
  const {
    open: assessmentOpen,
    openAssessment, closeAssessment, onComplete: onAssessmentComplete,
  } = useTravelAssessment({ openOnScroll: false });

  const openTwoDoors = (entry) => {
    if (entry) {
      try { sessionStorage.setItem("steck_2d_entry", entry); } catch (e) { /* ignore */ }
    }
    openAssessment("tab");
  };

  return (
  <Layout>
    <TwoDoorsExperience open={assessmentOpen} onClose={closeAssessment} onComplete={onAssessmentComplete} />
    {/* Phones: fixed reopen tab in the old "Gauge your needs" spot (the in-hero
        widget below is desktop-only). */}
    <TwoDoorsCollapsed mobileTab visible={!assessmentOpen} onOpen={openTwoDoors} />
    <SEO
      title="Beechjet 400A—Now Available | Steck Aviation Management"
      description="2026 refurbished Beechjet 400A light jet now available. Garmin G5000 avionics, turn-key ready. Contact Steck Aviation Management in Mineola, Texas."
      image="/images/bj400a-nose.png"
      schema={{
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "Beechjet 400A",
        "description": "2026 refurbished Beechjet 400A light jet. Garmin G5000 integrated avionics suite, turn-key ready.",
        "brand": { "@type": "Brand", "name": "Beechcraft" },
        "offers": {
          "@type": "Offer",
          "price": "800000",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock",
          "seller": { "@type": "Organization", "name": "Steck Aviation Management LLC", "url": "https://www.steckaviationllc.com" }
        }
      }}
    />
    <style>{ANIM_STYLES}</style>

    {/* ════ HERO ════════════════════════════════════════════════════════════ */}
    <style>{`
      /* Mobile: top-align the hero content and pad it below the absolutely
         positioned "NOW AVAILABLE" badge, so the badge is fully visible on load
         with a clear gap above the title — never behind it. Desktop keeps its
         bottom-aligned, full-height cinematic composition. */
      @media (max-width: 767px) {
        .aircraft-hero {
          justify-content: flex-start !important;
          padding-top: calc(clamp(96px,11vh,140px) + 54px) !important;
          padding-bottom: clamp(48px,7vh,80px) !important;
        }
      }
    `}</style>
    <section className="aircraft-hero" style={{
      minHeight: "100vh", background: "#000",
      display: "flex", flexDirection: "column", justifyContent: "flex-end",
      padding: `0 ${PAD} clamp(60px,8vh,120px)`,
      position: "relative", overflow: "hidden",
    }}>
      <CinematicPhoto
        src={IMG.nose}
        alt="Beechjet 400A"
        brightness={0.60}
        saturate={0.88}
        objectPosition="center 42%"
        kenBurns="kb-slow"
        kenBurnsDuration="20s"
        feather="radial-gradient(ellipse 85% 90% at 52% 48%, black 40%, transparent 100%)"
      />
      {/* Additional hero gradient for text zone */}
      <div aria-hidden style={{
        position: "absolute", inset: 0, zIndex: 1,
        background: "linear-gradient(to top, #000 0%, rgba(0,0,0,0.75) 18%, rgba(0,0,0,0.15) 42%, transparent 65%), linear-gradient(to bottom, #000 0%, transparent 20%)",
      }} />
      <VintageGrain z={2} opacity={0.038} />

      <div style={{ position: "absolute", top: "clamp(96px,11vh,140px)", left: PAD, zIndex: 2 }}>
        <span style={{
          ...mono, fontWeight: 500,
          fontSize: "clamp(20px,2.2vw,30px)",
          letterSpacing: "0.26em",
          color: "#67E8F9",
        }}>
          NOW AVAILABLE
        </span>
      </div>

      <div style={{ position: "relative", zIndex: 2 }}>
        <h1 style={{
          ...display, fontSize: "clamp(64px,12vw,180px)", fontWeight: 900,
          lineHeight: 0.92, color: "#fff", margin: 0, letterSpacing: "-0.02em",
        }}>
          Beechjet<br />
          <span style={{ color: "rgba(255,255,255,0.22)" }}>400A.</span>
        </h1>
        <p style={{ maxWidth: 520, marginTop: 32, color: "rgba(255,255,255,0.82)", fontSize: 18, lineHeight: 1.84 }}>
          Fresh 2026 paint and interior, turn-key ready. Steck Aviation
          currently holds inventory in the 400A—a meticulously maintained
          light jet built for productive business travel.
        </p>
        {/* Cost CTA leads the row, filled gold against the two outline
            buttons beside it, so it reads as the one worth clicking first —
            grounded in the same cluster as the rest of the CTAs rather than
            floating alone above the fold. */}
        <div style={{ marginTop: 40, display: "flex", flexWrap: "wrap", gap: 12 }}>
          <a href="#ownership-basics" className="btn-cost" data-testid="aircraft-cta-see-costs"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("ownership-basics")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}>
            See the cost of ownership <ArrowDown size={13} />
          </a>
          <a href="#inquiry" className="btn-primary" data-testid="aircraft-cta-walkthrough"
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            Request walkthrough <ArrowRight size={13} />
          </a>
          <a href="/documents/beechjet-400a.pdf"
            target="_blank" rel="noopener noreferrer" className="btn-ghost"
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Download size={13} /> Aircraft profile (PDF)
          </a>
        </div>
      </div>

      {/* Two Doors widget — a small tab in the hero's empty right space. It
          scrolls with the page (absolute, not fixed) and is sized so it never
          covers the title, badge, or CTAs. Hidden on narrower screens. */}
      <div className="aircraft-td-widget">
        <TwoDoorsCollapsed visible={!assessmentOpen} onOpen={openTwoDoors} compact />
      </div>
      <style>{`
        .aircraft-td-widget { position: absolute; top: clamp(150px,17vh,232px); right: clamp(24px,5vw,80px); z-index: 4; }
        @media (max-width: 1000px) { .aircraft-td-widget { display: none; } }
      `}</style>
    </section>

    {/* ════ THE AIRCRAFT + CABIN ════════════════════════════════════════════
        Specs strip integrated: headline → data grid → oval cabin photo →
        "Designed for productive travel." → feature list. One unified section.
        Elements slideUp staggered as section scrolls into view.
    ════════════════════════════════════════════════════════════════════════ */}
    <section ref={specsRef} style={{ background: "#000", borderTop: "1px solid #000" }}>

      {/* Headline + description—breathe above the specs grid */}
      <div style={{ padding: `clamp(72px,9vh,128px) ${PAD} 0` }}>
        <div style={{ animation: specsVisible ? "slideUp 0.9s 0s ease-out both" : "none" }}>
          <h2 style={{ ...display, fontSize: "clamp(32px,4.4vw,64px)", fontWeight: 800, color: "#fff", margin: "0 0 20px", lineHeight: 1.05 }}>
            Built for business<br />that can&rsquo;t wait.
          </h2>
        </div>
        <p style={{
          color: "rgba(255,255,255,0.80)", fontSize: 18, lineHeight: 1.82, margin: "0 0 clamp(44px,5.5vh,80px)", maxWidth: 540,
          animation: specsVisible ? "slideUp 0.9s 0.12s ease-out both" : "none",
        }}>
          Light jet category. Garmin G5000 glass cockpit. Full 2026 refurbishment.
        </p>

        {/* Specs—glass instrument panel */}
        <div style={{
          marginTop: "clamp(44px,5.5vh,72px)",
          position: "relative",
          background: "linear-gradient(135deg, rgba(255,255,255,0.030) 0%, rgba(103,232,249,0.016) 50%, rgba(255,255,255,0.010) 100%)",
          border: "1px solid rgba(103,232,249,0.16)",
          boxShadow: "0 0 0 1px rgba(103,232,249,0.06), inset 0 1px 0 rgba(255,255,255,0.045), 0 28px 72px rgba(0,0,0,0.44)",
          overflow: "hidden",
        }}>
          {/* Top accent rule */}
          <div aria-hidden style={{ height: 1, background: "linear-gradient(to right, transparent 0%, rgba(103,232,249,0.52) 30%, rgba(103,232,249,0.52) 70%, transparent 100%)" }} />
          <div className="aircraft-spec-grid">
            {[
              { num: "9",      unit: "PAX",     label: "PASSENGERS"   },
              { num: "440",    unit: "KTS",     label: "CRUISE SPEED" },
              { num: "1,519",  unit: "NM",      label: "RANGE"        },
              { num: "4,020",  unit: "FT/MIN",  label: "CLIMB RATE"   },
              { num: "45,000", unit: "FT",      label: "CEILING"      },
            ].map(({ num, unit, label }, i) => (
              <div key={`${label}-${i}`} className="aircraft-spec-cell" style={{
                animation: specsVisible ? `slideUp 0.75s ${0.14 + i * 0.07}s ease-out both` : "none",
              }}>
                <div className="aircraft-spec-num" style={{
                  ...display, fontWeight: 800, color: "#fff", letterSpacing: "-0.025em",
                }}>
                  {num}
                </div>
                <div className="aircraft-spec-unit" style={{ ...mono, color: "#7DD8EA", fontWeight: 600 }}>
                  {unit}
                </div>
                <div className="aircraft-spec-label" style={{ ...mono, color: GOLD, fontWeight: 500 }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
          {/* Bottom accent rule */}
          <div aria-hidden style={{ height: 1, background: "linear-gradient(to right, transparent 0%, rgba(103,232,249,0.18) 30%, rgba(103,232,249,0.18) 70%, transparent 100%)" }} />
        </div>

        <style>{`
          .aircraft-spec-grid {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 1px;
            background: rgba(103,232,249,0.09);
          }
          .aircraft-spec-cell {
            background: #000;
            padding: clamp(28px,3.8vh,52px) clamp(16px,2vw,28px);
            min-width: 0;
          }
          .aircraft-spec-num {
            /* Sized to keep even the widest value ("45,000") on a single
               line at every width the 5-column layout runs (down to the
               900px breakpoint), so a trailing zero can never drop to a
               second line. white-space:nowrap enforces it; tabular-nums
               keeps every digit the same width so the five numbers read as
               one uniform set rather than each sizing to its own glyphs. */
            font-size: clamp(26px,3.5vw,54px);
            line-height: 1;
            margin-bottom: 10px;
            white-space: nowrap;
            font-variant-numeric: tabular-nums;
          }
          .aircraft-spec-unit {
            font-size: clamp(14px,1.3vw,19px);
            letter-spacing: 0.11em;
            margin-bottom: 8px;
          }
          .aircraft-spec-label {
            font-size: clamp(11px,0.9vw,14px);
            letter-spacing: 0.13em;
          }
          @media (max-width: 900px) {
            .aircraft-spec-cell { padding: clamp(20px,3vh,36px) clamp(8px,1.4vw,16px); }
            /* Five columns at this width leave less room per cell than the
               old three-column tablet layout did—sized (and kept to one
               line) so "45,000" and "4,020" never wrap mid-number. */
            .aircraft-spec-num { font-size: clamp(15px,3.6vw,26px); white-space: nowrap; }
            .aircraft-spec-unit { font-size: clamp(9px,1.5vw,13px); }
            .aircraft-spec-label { font-size: clamp(7px,1.2vw,10px); }
          }
          @media (max-width: 560px) {
            .aircraft-spec-grid { grid-template-columns: repeat(2, 1fr); }
            .aircraft-spec-cell { padding: 22px 12px; }
            .aircraft-spec-num { font-size: clamp(22px,8vw,40px); }
            /* Five panels don't divide evenly into two columns—give the
               closing panel (Ceiling) the full width of its own row rather
               than leaving it stranded next to an empty cell. */
            .aircraft-spec-cell:last-child { grid-column: 1 / -1; }
          }
        `}</style>
      </div>

      {/* Bottom breathing room */}
      <div style={{ height: "clamp(24px,5vh,72px)" }} />
    </section>

    {/* ════ ROUTES—Efficient Access from East Texas ═════════════════════════
        Moved directly beneath the specification panel above so range/speed
        data and real-world reach read together as one continuous story.
    ════════════════════════════════════════════════════════════════════════ */}
    <section style={{
      background: "#000", borderTop: "1px solid #000", position: "relative", overflow: "visible",
      paddingTop: `clamp(64px,8vw,120px)`, paddingBottom: `clamp(170px,17vw,230px)`,
      paddingLeft: PAD, paddingRight: PAD,
    }}>
      {/* Green satin wash—static, feathered, organic in shape (no hard
          edges), with real grain texture instead of a flat gradient, plus a
          slow diagonal sheen that sweeps the section's full width rather
          than a small rotating "flashlight"—it reads as light passing
          over the whole panel, accentuating how wide the section is. This
          wrapper's own overflow:hidden clips everything to this section's
          box exactly—none of it can bleed into the specs panel above or
          the Command section below. */}
      <div ref={routesSheenRef} aria-hidden style={{
        position: "absolute", inset: 0, zIndex: 0, overflow: "hidden", pointerEvents: "none",
        // Fade the whole green wash + diagonal sweep out toward the bottom so
        // the scan beam dissolves into the black band ahead of the Command
        // section instead of being hard-clipped at the section's edge.
        WebkitMaskImage: "linear-gradient(to bottom, #000 0%, #000 58%, rgba(0,0,0,0.4) 80%, transparent 96%)",
        maskImage: "linear-gradient(to bottom, #000 0%, #000 58%, rgba(0,0,0,0.4) 80%, transparent 96%)",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          background: [
            "radial-gradient(ellipse 65% 60% at 20% 10%, rgba(110,255,180,0.05) 0%, transparent 62%)",
            "radial-gradient(ellipse 55% 65% at 85% 45%, rgba(110,255,180,0.04) 0%, transparent 65%)",
            "radial-gradient(ellipse 80% 55% at 40% 95%, rgba(110,255,180,0.045) 0%, transparent 62%)",
            "radial-gradient(ellipse 70% 50% at 95% 100%, rgba(110,255,180,0.035) 0%, transparent 60%)",
          ].join(", "),
        }} />
        {/* Real grain, not a smooth gradient—the noise modulates the green
            wash above via overlay blending, giving it actual texture. */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: GRAIN_URL, backgroundSize: "160px 160px",
          mixBlendMode: "overlay", opacity: 0.16,
        }} />
        <div className={`routes-sheen${routesSheenVisible ? " is-in-view" : ""}`} />
      </div>
      <style>{`
        /* One calm, slow pass—a long, unhurried drift across the section's
           full width, then a long quiet hold before it repeats. Paused by
           default and only released once this section has actually scrolled
           into view (.is-in-view, added on first IntersectionObserver hit) —
           otherwise the 100s clock runs from page load, and a visitor who
           takes a while to scroll down arrives mid-cycle (or during the long
           hold) and never sees the pass at all. Because a paused CSS
           animation doesn't advance its own clock, releasing it always
           starts the sweep fresh from 0%, speed unchanged. */
        .routes-sheen {
          position: absolute;
          top: -25%;
          left: -35%;
          width: 30%;
          height: 150%;
          background: linear-gradient(78deg,
            transparent 0%,
            rgba(140,255,205,0.05) 42%,
            rgba(140,255,205,0.10) 50%,
            rgba(140,255,205,0.05) 58%,
            transparent 100%);
          filter: blur(10px);
          mix-blend-mode: screen;
          /* No lead-in delay and a much faster cross (was 46s + 0.6s delay,
             100s before that) so the beam begins its pass the instant the
             section reaches view — a fast scroller catches it happening
             rather than arriving to a still panel and waiting. Then a quiet
             hold before it repeats. */
          animation: routes-sheen-sweep 20s ease-in-out infinite;
          animation-play-state: paused;
        }
        .routes-sheen.is-in-view {
          animation-play-state: running;
        }
        @keyframes routes-sheen-sweep {
          0%   { transform: translateX(0%); }
          48%  { transform: translateX(430%); }
          100% { transform: translateX(430%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .routes-sheen { animation: none; }
        }
      `}</style>
      {/* Texas technical map—anchored here, bleeds upward into the specs panel */}
      <div aria-hidden style={{
        position: "absolute",
        right: "-4%",
        top: "clamp(-340px,-38vh,-200px)",
        width: "clamp(420px,52vw,720px)",
        height: "calc(100% + clamp(340px,38vh,200px) + 60px)",
        zIndex: 0,
        pointerEvents: "none",
        WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.30) 12%, rgba(0,0,0,0.72) 24%, black 38%, black 82%, transparent 100%)",
        maskImage: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.30) 12%, rgba(0,0,0,0.72) 24%, black 38%, black 82%, transparent 100%)",
      }}>
        <TexasMap showLabels={false} />
      </div>
      {/* Narrower than this page's usual 1500px content width on purpose —
          more dark margin left/right so the panels feel a touch more
          contained and boutique, matching the quieter climate this section
          is going for, rather than stretching edge-to-edge like the rest
          of the page. */}
      <div style={{ maxWidth: 1280, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <div className="routes-heading">
          <h2 style={{ ...display, fontSize: "clamp(32px,4vw,56px)", fontWeight: 800, color: "#fff", margin: "0 0 16px", lineHeight: 1.1 }}>
            Efficient Access from East Texas
          </h2>
          <p style={{ color: "rgba(255,255,255,0.72)", maxWidth: 560, marginBottom: 48, fontSize: 17, lineHeight: 1.78 }}>
            The 400A&apos;s range opens the continental U.S. from your home
            field. Indicative flight times at cruise.
          </p>
        </div>
        <div className="routes-grid-wrap">
          <div className="routes-grid">
            {/* Synced with the homepage flight calculator: WGS84 geodesic
                distance (NM) from Wood County Airport (KJDD, our origin) to each
                airport, the same Beechjet 400A max-cruise time model, and each
                field's true bearing. Keep these in step with EAST_TEXAS_ORIGIN +
                FLIGHT_PLANNING_MODEL in Home.jsx. */}
            {[
              ["Dallas",      "DAL", 265, "68 nm",   "18 min" ],
              ["Houston",     "HOU", 177, "198 nm",  "36 min" ],
              ["New Orleans", "MSY", 122, "321 nm",  "53 min" ],
              ["Nashville",   "BNA", 64,  "477 nm",  "1.2 hrs"],
              ["Atlanta",     "ATL", 83,  "558 nm",  "1.4 hrs"],
              ["Denver",      "DEN", 316, "607 nm",  "1.6 hrs"],
              ["Chicago",     "ORD", 31,  "651 nm",  "1.7 hrs"],
              ["Miami",       "MIA", 114, "903 nm",  "2.2 hrs"],
              ["New York",    "JFK", 60,  "1140 nm", "2.8 hrs"],
            ].map(([city, code, bearing, miles, time]) => (
              <div key={city}
                data-testid={`route-${city.toLowerCase().replace(/\s+/g, "-")}`}
                className="routes-grid__cell"
              >
                <div>
                  <div style={{ ...display, fontSize: "clamp(26px,2.6vw,34px)", fontWeight: 700, color: "#fff", lineHeight: 1.1 }}>{city}</div>
                  <div style={{ fontSize: 15, color: "rgba(255,255,255,0.72)", marginTop: 8 }}>{miles}</div>
                </div>
                <svg className="routes-grid__pulse" viewBox="0 0 120 120" aria-hidden>
                  <circle cx="60" cy="60" r="28" fill="none" stroke="rgba(103,232,249,0.6)" strokeWidth="0.4" />
                </svg>
                <CityReticle code={code} city={city} bearing={bearing} miles={parseInt(miles, 10)} />
                <div style={{ ...display, fontSize: "clamp(16px,1.5vw,20px)", color: "#7DD8EA", fontWeight: 600, letterSpacing: "-0.01em" }}>{time}</div>
              </div>
            ))}
          </div>
        </div>
        <style>{`
          /* 9 destinations—3×3 (or a single column on mobile) so the grid
             always divides evenly and never leaves a dangling empty cell. */

          /* Subtle floating aura—a very slow, low-amplitude drift with a
             soft breathing glow behind it. Reads as ambient life, not motion. */
          .routes-grid-wrap {
            position: relative;
          }
          .routes-grid-wrap::before {
            content: "";
            position: absolute;
            inset: -8% -4%;
            background: radial-gradient(ellipse 65% 60% at 50% 45%, rgba(103,232,249,0.14) 0%, rgba(103,232,249,0.04) 45%, transparent 75%);
            filter: blur(36px);
            opacity: 0.55;
            animation: routes-aura-breathe 9s ease-in-out infinite;
            pointer-events: none;
            z-index: 0;
          }
          .routes-grid {
            position: relative;
            z-index: 1;
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 1px;
            background: rgba(255,255,255,0.06);
          }
          /* The float used to live on .routes-grid itself, but animating
             transform on a grid built from 1px hairline gaps makes those
             thin dividers shimmer/flicker each frame as they get
             re-antialiased at fractional pixel offsets. Moving the same
             float to the heading (no hairlines to catch the artifact)
             keeps the section's gentle motion without the flicker, and the
             grid itself now stays perfectly static and crisp. */
          .routes-heading {
            animation: routes-float 9s ease-in-out infinite;
            will-change: transform;
          }
          .routes-grid__cell {
            position: relative;
            overflow: hidden;
            background: rgba(0,0,0,0.965);
            padding: 36px 32px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            transition: background 400ms ease;
          }
          .routes-grid__cell:hover {
            background: rgba(255,255,255,0.045);
          }
          /* City reticle—correct to its own cell's city every time, fades
             and settles in on hover instead of a shared, misaligned backdrop. */
          .routes-grid__reticle {
            position: absolute;
            left: 50%;
            top: 50%;
            width: 148px;
            height: 88.8px;
            transform: translate(-50%,-50%) scale(0.92);
            opacity: 0;
            transition: opacity 420ms ease, transform 420ms ease;
            pointer-events: none;
          }
          .routes-grid__cell:hover .routes-grid__reticle {
            opacity: 1;
            transform: translate(-50%,-50%) scale(1);
          }
          /* Pulse wave—a slow, hairline-thin ring (SVG stroke stays crisp
             at any zoom, unlike a CSS border) that radiates outward from the
             reticle every couple of seconds while hovered. The cell's own
             overflow:hidden clips it at that panel's edge, so it never
             bleeds into a neighboring city. Only runs while hovered. */
          .routes-grid__pulse {
            position: absolute;
            left: 50%;
            top: 50%;
            width: 60px;
            height: 60px;
            margin: -30px 0 0 -30px;
            opacity: 0;
            pointer-events: none;
          }
          .routes-grid__cell:hover .routes-grid__pulse {
            animation: routes-pulse-wave 2.6s cubic-bezier(.22,.61,.36,1) infinite;
          }
          @keyframes routes-float {
            0%, 100% { transform: translateY(0); }
            50%      { transform: translateY(-10px); }
          }
          @keyframes routes-aura-breathe {
            0%, 100% { opacity: 0.40; }
            50%      { opacity: 0.80; }
          }
          @keyframes routes-pulse-wave {
            0%   { opacity: 0;    transform: scale(0.6); }
            14%  { opacity: 0.5;  transform: scale(1.4); }
            100% { opacity: 0;    transform: scale(15); }
          }
          @media (max-width: 700px) {
            .routes-grid { grid-template-columns: 1fr; }
            .routes-grid__reticle { width: 108px; height: 64.8px; }
          }
          @media (prefers-reduced-motion: reduce) {
            .routes-heading { animation: none; }
            .routes-grid-wrap::before { animation: none; }
            .routes-grid__reticle { transition: opacity 420ms ease; }
            .routes-grid__cell:hover .routes-grid__reticle { transform: translate(-50%,-50%) scale(1); }
            .routes-grid__cell:hover .routes-grid__pulse { animation: none; }
          }
        `}</style>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,0.66)", marginTop: 22, lineHeight: 1.74, maxWidth: 720 }}>
          Estimated flight times and trip costs are provided for planning purposes and may vary
          based on routing, weather patterns, winds, airport requirements, fuel pricing, aircraft
          availability, scheduling, overnight accommodations, deicing, and other operational
          conditions.
        </p>
      </div>

      {/* Cinematic exit fade—the only soft element in an otherwise hard-edged,
          modular section. Confined to the empty padding beneath the disclaimer
          (the grid and text above it stay fully crisp), then spills slightly
          past the section's own bottom edge into the cockpit photo below, so
          the seam between the green texture and the image dissolves instead
          of cutting. */}
      <div aria-hidden style={{
        position: "absolute", left: 0, right: 0, bottom: "-64px", height: "220px",
        background: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.35) 42%, rgba(0,0,0,0.75) 68%, #000 100%)",
        zIndex: 5, pointerEvents: "none",
      }} />
    </section>

    {/* ════ 02 · COMMAND ═══════════════════════════════════════════════════════
        Cockpit photo starts assembled, shatters as you scroll through.
    ════════════════════════════════════════════════════════════════════════ */}
    <section ref={commandRef} style={{ background: "#000", borderTop: "1px solid #000" }}>
      <CommandIris commandVisible={commandVisible} />
    </section>

    {/* ════ 02.5 · WHAT'S INSIDE—Cabin assembly ══════════════════════════
        Cabin interior shards converge as section enters view.
    ════════════════════════════════════════════════════════════════════════ */}
    <section style={{ background: "#000", borderTop: "1px solid #000" }}>
      <CabinShatter />
      <div style={{ height: "clamp(24px,3vh,48px)" }} />
    </section>

    {/* ════ CABIN WALKTHROUGH—autoplaying interior walk ═══════════════════
        Sits between What's Inside and The Envelope (its documented home): a
        "step aboard the actual airplane" beat that plays the cabin→flight-deck
        interior clip in view. Its own bottom feather resolves into black, which
        The Envelope's entry fade then carries into the closing line below.
    ════════════════════════════════════════════════════════════════════════ */}
    <CabinWalkthrough />

    {/* ════ THE ENVELOPE—closing line ═════════════════════════════════════
        Third and final beat of the Command. / What's Inside. / The Envelope.
        sequence. No photo—but no longer bare black either: a slow
        high-altitude glow gives it the same sense of atmosphere the two
        photographic beats before it have, a reprise of the aircraft's own
        performance figures gives the pause something to say, and a soft
        fade at the top now eases the handoff out of the cabin photo above
        instead of cutting straight to flat black.
    ════════════════════════════════════════════════════════════════════════ */}
    <section ref={envRef} style={{ background: "#000", borderTop: "1px solid #000", position: "relative", overflow: "hidden" }}>
      {/* Entry fade—resolves the cabin photo above into black before the
          glow begins, so the cut into this section reads as a dissolve. */}
      <div aria-hidden style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "22%",
        background: "linear-gradient(to bottom, #000 0%, transparent 100%)",
        pointerEvents: "none", zIndex: 2,
      }} />
      {/* Focus dim—as "The Envelope." rises into frame, the whole band above
          it deepens to pure black so the heading is isolated in the void.
          Driven by the heading's own view-timeline (below), so it ramps to
          full exactly as the type arrives. Sits above the glow/grain (z 2) but
          below the heading (z 3), so only the space above the type darkens;
          fully transparent by the heading's own top edge so the type and the
          glow immediately behind it stay lit. */}
      <div aria-hidden className="envelope-dim" style={{
        position: "absolute", top: 0, left: 0, right: 0,
        height: "clamp(300px,50vh,620px)",
        background: "linear-gradient(to bottom, #000 0%, #000 62%, transparent 100%)",
        pointerEvents: "none", zIndex: 2,
      }} />
      {/* High-altitude glow—the only "visual" in an otherwise photo-free
          section. Faint, slow, and centered behind the type, it reads as
          thin air at 45,000 feet rather than a decorative flourish. */}
      <div aria-hidden className="envelope-glow" style={{
        position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none",
        background: "radial-gradient(ellipse 60% 46% at 50% 40%, rgba(103,232,249,0.09) 0%, rgba(103,232,249,0.03) 45%, transparent 72%)",
      }} />
      <VintageGrain z={1} opacity={0.035} />
      <div style={{
        position: "relative", zIndex: 3,
        // Heading sits ~half a viewport down from the section top, so by the
        // time it reaches the centre of the frame the section itself fills
        // everything above it—no previous section peeking in—and the focus dim
        // blackens that whole band. The heading emerges from a clean void.
        paddingTop: `clamp(300px,50vh,620px)`,
        // A real held beat, not a token gap—"The Envelope." should be
        // essentially alone and mostly scrolled past before "Ownership
        // Basics." starts to surface at the very bottom of the frame.
        paddingBottom: `clamp(260px,34vh,400px)`,
        paddingLeft: PAD, paddingRight: PAD,
        textAlign: "center",
      }}>
        {/* Scroll-reveal entrance lives on the wrapper; the specular sheen
            lives on the title itself, so the two animations never contend for
            the same animation slot (which would break one of them). */}
        <div ref={envHeadRef} className="envelope-headwrap">
          <h2 className={`envelope-wipe${envHeadVisible ? " is-revealed" : ""}`} style={{
            ...display, fontSize: "clamp(44px,6.4vw,104px)", fontWeight: 900, color: "#fff",
            lineHeight: 0.9, letterSpacing: "-0.024em", margin: 0, display: "inline-block",
          }}>
            The Envelope.
          </h2>
        </div>
      </div>
      {/* Thematic bridge—the cool steel-blue the Ownership section is built
          from, foreshadowed and built up gradually across the lower half of
          this section so entering cost/ownership territory reads as one
          continuous atmosphere resolving into focus, not a hard cut into a
          separate panel. Reaches a defined steel-blue exactly at this
          section's bottom edge, and the next section's own background picks
          up at that same value—so the two meet seamlessly with no black
          band and no visible line. */}
      <div aria-hidden style={{
        position: "absolute", left: 0, right: 0, bottom: 0, height: "clamp(420px,52vh,620px)",
        // Fixed-height rise (px-predictable stops, not a % of this very tall
        // section) that DECELERATES into the seam—the last segments flatten
        // out (0.12→0.135→0.15) so the brightness slope is already near-zero
        // by the time it reaches the bottom edge. The next section leaves that
        // edge at the same value and the same near-flat slope, so there is no
        // corner in the brightness profile for the eye to read as a line.
        background: "linear-gradient(to bottom, transparent 0%, rgba(78,112,158,0.02) 34%, rgba(78,112,158,0.06) 60%, rgba(78,112,158,0.10) 78%, rgba(78,112,158,0.135) 92%, rgba(78,112,158,0.15) 100%)",
        pointerEvents: "none", zIndex: 1,
      }} />
      <style>{`
        .envelope-glow {
          animation: envelope-glow-breathe 12s ease-in-out infinite;
          will-change: opacity, transform;
        }
        /* The glow now also breathes in scale—thin air gently expanding and
           settling, giving the section quiet life without any HUD/radar read. */
        @keyframes envelope-glow-breathe {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.05); }
        }
        @media (prefers-reduced-motion: reduce) {
          .envelope-glow { animation: none; transform: none; }
        }
        /* Envelope title—revealed LEFT-TO-RIGHT as it scrolls into frame: a
           clean wipe uncovering the type from its left edge across to the
           period, scrubbed by scroll position. Vertical clip bounds are pushed
           out (±25%) so ascenders/descenders (the "p") are never clipped, and
           the default / unsupported state is fully shown (never hidden). */
        .envelope-wipe {
          clip-path: inset(-25% 100% -25% 0);
          /* Slower, Apple-style reveal: an easeOutExpo curve—moves off promptly
             then decelerates into a long, graceful settle. */
          transition: clip-path 1750ms cubic-bezier(0.16, 1, 0.3, 1);
          will-change: clip-path;
        }
        .envelope-wipe.is-revealed { clip-path: inset(-25% -2% -25% 0); }
        @media (prefers-reduced-motion: reduce) {
          .envelope-wipe { clip-path: none; transition: none; }
        }
        /* Focus dim tied to the heading's OWN scroll progress: the heading
           publishes a view-timeline, and the dim band above it scrubs its
           opacity 0→1 across that timeline—so it deepens to pure black exactly
           as "The Envelope." comes into frame, then holds. */
        .envelope-headwrap { view-timeline-name: --envhead; view-timeline-axis: block; }
        .envelope-dim { opacity: 0; will-change: opacity; }
        @supports (animation-timeline: view()) {
          .envelope-dim {
            animation: envelope-dim-in linear both;
            animation-timeline: --envhead;
            animation-range: entry 20% entry 95%;
          }
        }
        @keyframes envelope-dim-in {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .envelope-dim { opacity: 0; animation: none; }
        }
        /* Default: fully visible—a safe baseline if neither the scroll-
           timeline nor the IntersectionObserver fallback ever fires. */
        .scroll-reveal {
          will-change: transform, opacity;
        }
        /* Modern browsers: the reveal is scrubbed directly by scroll
           position. Deliberately no blur—a calm opacity fade and a small
           rise so text stays crisp and readable the entire time; the motion
           is a confirmation the content has arrived, not a delay before you
           can read it. */
        @supports (animation-timeline: view()) {
          .scroll-reveal {
            opacity: 0;
            transform: translateY(16px);
            animation: scroll-reveal-in linear both;
            animation-timeline: view();
            animation-range: entry 0% cover 34%;
          }
        }
        @keyframes scroll-reveal-in {
          0%   { opacity: 0; transform: translateY(16px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        /* Fallback for browsers without animation-timeline support. */
        .scroll-reveal.is-in-view {
          opacity: 1;
          transform: translateY(0);
          transition: opacity 600ms cubic-bezier(.2,.7,.2,1),
                      transform 600ms cubic-bezier(.2,.7,.2,1);
        }
        @media (prefers-reduced-motion: reduce) {
          .scroll-reveal {
            opacity: 1 !important; transform: none !important; animation: none !important;
          }
        }
      `}</style>
    </section>

    {/* ════ OWNERSHIP BASICS ════════════════════════════════════════════════ */}
    <section id="ownership-basics" style={{
      // Accounted for by scrollIntoView automatically—keeps the section
      // title clear of the fixed 88px nav bar instead of tucking under it.
      scrollMarginTop: 108,
      background: [
        // Seam continuity — the top edge leaves the seam at the SAME value
        // (0.15) and the SAME near-flat slope The Envelope's bridge arrives
        // with, so the two sections read as one continuous brightness curve
        // with no corner, no peak, and no line at the boundary. It holds the
        // steel-blue briefly through the title, then eases smoothly down into
        // the sheet. Pixel-based stops (not % of this very tall section) keep
        // the falloff predictable regardless of how much content sits below.
        "linear-gradient(to bottom, rgba(78,112,158,0.15) 0px, rgba(78,112,158,0.152) 70px, rgba(78,112,158,0.14) 210px, rgba(78,112,158,0.092) 400px, rgba(78,112,158,0.035) 600px, transparent 820px)",
        "#000",
      ].join(", "),
      // No borderTop — a hard 1px line at the seam is exactly the blocky
      // edge this transition is meant to erase.
      padding: `clamp(96px,11vw,160px) ${PAD} clamp(64px,8vw,120px)`,
    }}>
      <div style={{ maxWidth: 1500, margin: "0 auto" }}>
        {/* Ownership panel—title header + cost data as ONE continuous, formally
            organized block, so the section reads as a single fund fact sheet
            rather than a floating title box hovering above a separate data
            card. The whole panel emerges from the deep cinematic black via a
            tall top feather (mask), carrying the Envelope's darkness through
            into the cost presentation rather than snapping into a lit card. The
            title keeps its own soft steel-blue spotlight so it is never naked,
            but now sits as the sheet's header rather than a detached box, and
            the $800K data reads as its uniform, formally structured body. */}
        <div style={{
          position: "relative",
          display: "flex", flexDirection: "column", gap: "1px", background: "rgba(255,255,255,0.055)",
          maskImage: "linear-gradient(to bottom, transparent 0, #000 132px)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0, #000 132px)",
        }}>

          {/* Title header—first row of the panel, with its own soft spotlight
              so it is never naked. Generous top padding so the title text
              clears the panel's top feather and sits at full strength. */}
          <div style={{ position: "relative", overflow: "hidden", padding: "clamp(84px,9vw,128px) 40px 44px" }}>
            <div aria-hidden style={{
              position: "absolute", inset: 0,
              background: "radial-gradient(ellipse 62% 140% at 28% 64%, rgba(78,112,158,0.17) 0%, transparent 66%)",
              pointerEvents: "none",
            }} />
            <h2 className="scroll-reveal" style={{ position: "relative", ...display, fontSize: "clamp(42px,5.2vw,76px)", fontWeight: 900, color: "#fff", margin: 0, lineHeight: 1.08, letterSpacing: "-0.015em" }}>
              Ownership <span style={{ color: GOLD }}>Made Easy.</span>
            </h2>
          </div>

          {/* 1 · SHARE PRICE—stands alone now. Ownership Agreements has moved
              to the closing block below, and the funds block that used to close
              this sheet now lives on the Operating Outlook page. */}
          <div className="scroll-reveal" style={{ padding: "48px 40px" }}>
            <div style={{ ...mono, fontSize: "clamp(14px,1.15vw,17px)", letterSpacing: "0.10em", color: GOLD, marginBottom: 22, fontWeight: 600 }}>SHARE PRICE</div>
            <div style={{ ...display, fontSize: "clamp(48px,6vw,88px)", fontWeight: 900, color: "#fff", lineHeight: 1 }}>$800,000</div>
            <div style={{ ...mono, fontSize: "clamp(14px,1.1vw,16px)", color: "rgba(255,255,255,0.76)", marginTop: 16, letterSpacing: "0.06em", fontWeight: 500 }}>PER 25% OWNERSHIP SHARE</div>
          </div>

          {/* 2 · OPERATING COSTS—Fixed Monthly / Hourly Flight Cost / Pilots and Pilot Expenses
              No cell fill here either—the section's glow carries through this
              block the same way it does Share Price, rather than hitting a
              black panel. */}
          <div className="scroll-reveal" style={{ background: "transparent", padding: "48px 40px" }}>
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ ...display, fontSize: "clamp(24px,2.6vw,34px)", fontWeight: 700, color: "#fff", margin: 0, lineHeight: 1.15 }}>
                Operating Costs
              </h3>
            </div>
            {/* Signifier rule—Operating Costs gets the same anchor treatment
                as the section title above, so it reads as its own grounded
                header rather than a floating line of text. */}
            <div aria-hidden style={{ height: 2, marginBottom: 40, background: "linear-gradient(to right, rgba(200,168,112,0.55) 0%, rgba(200,168,112,0.55) 40%, transparent 92%)" }} />
            <div className="ownership-3col ownership-3col--glow">
              <div style={{ padding: "36px 32px" }}>
                <div style={{ ...mono, fontSize: "clamp(14px,1.15vw,17px)", letterSpacing: "0.10em", color: GOLD, marginBottom: 18, fontWeight: 600 }}>FIXED MONTHLY</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <div style={{ ...display, fontSize: "clamp(40px,5vw,64px)", fontWeight: 800, color: "#fff", lineHeight: 1 }}>$2,250</div>
                  <div style={{ ...mono, fontSize: "clamp(12px,0.95vw,14px)", color: "rgba(200,168,112,0.75)", letterSpacing: "0.05em", fontWeight: 600 }}>/ MO</div>
                </div>
                <div className="cost-rule" aria-hidden />
                <div style={{ color: "rgba(255,255,255,0.76)", fontSize: "clamp(15px,1.15vw,17px)", lineHeight: 1.76 }}>One predictable figure for the standing costs of readiness—hangar, insurance, cleaning, stocking, avionics updates, and pilot training. It holds steady month to month, whether the aircraft flies or sits.</div>
              </div>
              <div className="cost-col" style={{ padding: "36px 32px" }}>
                <div style={{ ...mono, fontSize: "clamp(14px,1.15vw,17px)", letterSpacing: "0.10em", color: GOLD, marginBottom: 18, fontWeight: 600 }}>HOURLY FLIGHT COST</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <div style={{ ...display, fontSize: "clamp(40px,5vw,64px)", fontWeight: 800, color: "#fff", lineHeight: 1 }}>$2,150</div>
                  <div style={{ ...mono, fontSize: "clamp(12px,0.95vw,14px)", color: "rgba(200,168,112,0.75)", letterSpacing: "0.05em", fontWeight: 600 }}>/ FLT HR</div>
                </div>
                <div className="cost-rule" aria-hidden />
                <div style={{ color: "rgba(255,255,255,0.76)", fontSize: "clamp(15px,1.15vw,17px)", lineHeight: 1.76 }}>
                  The direct cost of flight itself—fuel<span style={{ color: "rgba(200,168,112,0.9)" }}>*</span>,
                  maintenance reserves, and engine reserves. Billed monthly on actual
                  usage, so it always reflects the flying you do—never prepaid, never drawn from the
                  operating reserve.
                </div>
              </div>
              <div className="cost-col" style={{ padding: "36px 32px" }}>
                <div style={{ ...mono, fontSize: "clamp(14px,1.15vw,17px)", letterSpacing: "0.10em", color: GOLD, marginBottom: 18, fontWeight: 600 }}>PILOTS AND PILOT EXPENSES</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <div style={{ ...display, fontSize: "clamp(40px,5vw,64px)", fontWeight: 800, color: "#fff", lineHeight: 1, whiteSpace: "nowrap" }}>
                    By trip
                  </div>
                </div>
                <div className="cost-rule" aria-hidden />
                <div style={{ color: "rgba(255,255,255,0.76)", fontSize: "clamp(15px,1.15vw,17px)", lineHeight: 1.76 }}>
                  Pilots and their trip-related expenses are billed separately from the fixed and
                  hourly costs above, and are reviewed with you directly for each trip.
                </div>
              </div>
            </div>
            {/* Fuel disclosure — market-responsive pricing handled at Steck's
                discretion with transparent communication; excess usage noted
                subtly rather than emphasized. */}
            <div style={{ ...mono, fontSize: "clamp(11px,0.85vw,13px)", color: "rgba(255,255,255,0.44)", letterSpacing: "0.04em", lineHeight: 1.7, marginTop: 28, maxWidth: 760 }}>
              <span style={{ color: "rgba(200,168,112,0.9)" }}>*</span> Fuel pricing and related operating costs may be adjusted at Steck Aviation&rsquo;s discretion in response to market conditions. Any resulting cost considerations are communicated transparently, with fuel usage beyond the applicable allowance billed separately.
            </div>
          </div>

          {/* 3 · OWNERSHIP AGREEMENTS—closing block. Moved here from beside
              Share Price and given the same gold separation line the funds
              block carried, so the sheet still closes on a clear statement.
              (The funds block itself now lives on the Operating Outlook page.) */}
          <div className="scroll-reveal" style={{
            position: "relative",
            background: "transparent",
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}>
            <div aria-hidden style={{ height: 2, background: "linear-gradient(to right, transparent 0%, rgba(200,168,112,0.60) 30%, rgba(200,168,112,0.60) 70%, transparent 100%)" }} />
            <div style={{ padding: "clamp(64px,8vh,108px) 40px clamp(48px,6vh,80px)" }}>
              <div style={{ ...mono, fontSize: "clamp(14px,1.15vw,17px)", letterSpacing: "0.10em", color: "#7DD8EA", marginBottom: 24, fontWeight: 600 }}>OWNERSHIP AGREEMENTS</div>
              <h3 style={{ ...display, fontSize: "clamp(30px,3.4vw,48px)", fontWeight: 700, color: "#fff", margin: "0 0 26px", lineHeight: 1.12, letterSpacing: "-0.01em" }}>
                Costs protected by written agreement.
              </h3>
              <p style={{ color: "rgba(255,255,255,0.78)", fontSize: "clamp(16px,1.2vw,18px)", lineHeight: 1.82, margin: 0, maxWidth: 880 }}>
                The agreement each owner executes protects the cost structure—funding, operating costs, and decision-making are set in writing, not left to informal arrangement among the owners. Reviewed with the owner&apos;s counsel before signing. Draft documents available to qualified prospects upon request.
              </p>
            </div>
          </div>

        </div>
      </div>

      <style>{`
        .ownership-2col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1px;
          background: rgba(255,255,255,0.06);
        }
        /* Share Price + Ownership Agreements sit right where the section's
           gold glow originates. No cell fill and no gap divider here—the
           glow needs to read as one continuous surface behind both columns,
           not two boxes cutting it off at their edges. */
        .ownership-2col--glow {
          background: transparent;
        }
        /* Vertical thread between Share Price and Ownership Agreements —
           same grammar as the gold rule under "Operating Costs" below: firm
           near its anchor, then dissolves rather than running the full
           distance. Left implied instead of fully drawn, so it reads as one
           quiet gesture, not a hard column divider. */
        .ownership-connector {
          position: absolute;
          left: 50%;
          top: clamp(150px,18vh,180px);
          height: clamp(90px,12vh,130px);
          width: 1px;
          transform: translateX(-50%);
          background: linear-gradient(to bottom, rgba(200,168,112,0.55) 0%, rgba(200,168,112,0.55) 40%, transparent 92%);
          pointer-events: none;
        }
        @media (max-width: 760px) {
          .ownership-connector { display: none; }
        }
        .ownership-3col {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1px;
          background: rgba(255,255,255,0.06);
        }
        /* Operating Costs and Operating Funds, same treatment as Share Price:
           no panel fill, no gap divider—one continuous surface so the
           section's glow reads as a single atmosphere the whole way down,
           not a strip that vanishes under the first black box it meets. */
        .ownership-3col--glow {
          background: transparent;
        }
        /* Reads as a cost/pricing table rather than loose text: a thin
           column divider (no fill, so the glow still passes through) and a
           short rule under each price separating the number from its
           explanation—the same grammar a pricing comparison table uses. */
        .cost-col {
          border-left: 1px solid rgba(200,168,112,0.16);
        }
        .cost-rule {
          width: 32px;
          height: 1px;
          background: rgba(200,168,112,0.40);
          margin: 18px 0 16px;
        }
        @media (max-width: 760px) {
          .ownership-2col,
          .ownership-3col { grid-template-columns: 1fr; }
          .cost-col { border-left: none; }
        }
      `}</style>
    </section>

    {/* Boarding detail—atmospheric transition */}
    <div style={{ position: "relative", height: "clamp(160px,22vh,280px)", overflow: "hidden", background: "#000" }}>
      <img
        src={IMG.boarding}
        alt="Aircraft boarding step"
        loading="lazy"
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          objectFit: "cover", objectPosition: "center 28%",
          filter: "brightness(0.32) contrast(1.2) saturate(0.5)",
        }}
      />
      <div aria-hidden style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(to bottom, #000 0%, transparent 35%, transparent 65%, #000 100%)",
      }} />
    </div>

    {/* ════ INQUIRY ═════════════════════════════════════════════════════════ */}
    <section id="inquiry" style={{ background: "#000", borderTop: "1px solid #000", padding: `clamp(64px,8vw,120px) ${PAD}` }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: "clamp(40px,6vw,96px)", alignItems: "start" }}>
        <div>
          <h2 style={{ ...display, fontSize: "clamp(28px,3.5vw,44px)", fontWeight: 800, color: "#fff", margin: "0 0 20px", lineHeight: 1.1 }}>
            Ready to learn more?
          </h2>
          <p style={{ color: "rgba(255,255,255,0.76)", fontSize: 17, lineHeight: 1.82, marginBottom: 36 }}>
            Contact us for full aircraft logs, engine times, and a personal
            walkthrough. We respond within one business day.
          </p>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 16 }}>
            <li style={{ display: "flex", alignItems: "center", gap: 12, color: "rgba(255,255,255,0.88)", fontSize: 16 }}>
              <Phone size={14} style={{ color: "#67E8F9", flexShrink: 0 }} /> 903-539-9000
            </li>
            <li style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 16 }}>
              <Mail size={14} style={{ color: "#67E8F9", flexShrink: 0 }} />
              <a className="editorial-link" href="mailto:contact@steckaviationllc.com">contact@steckaviationllc.com</a>
            </li>
            <li style={{ display: "flex", alignItems: "flex-start", gap: 12, color: "rgba(255,255,255,0.70)", fontSize: 15, lineHeight: 1.6 }}>
              <Plane size={14} style={{ color: "#67E8F9", flexShrink: 0, marginTop: 2 }} />
              <span>1251 Bromberg Street<br />Mineola, Texas 75773</span>
            </li>
          </ul>
        </div>
        <div>
          <InquiryForm report={{ slug: "beechjet-400a", title: "Beechjet 400A" }} variant="compact" />
        </div>
      </div>
    </section>

  </Layout>
  );
};

export default Aircraft;
